import { ConsolidatedForecastListItem, ConsolidatedLiveReportAvailableDatasource } from '@futrli-sdk/consolidations';
import { ForecastListItem, LiveReportAvailableDatasource } from '@futrli-sdk/predict';

export type ReportBuilderForecast = ForecastListItem | ConsolidatedForecastListItem;

export type AvailableDatasource = LiveReportAvailableDatasource | ConsolidatedLiveReportAvailableDatasource;

const FORECAST_DATASOURCE_KINDS: AvailableDatasource['kind'][] = ['organisation-forecast', 'consolidation-forecast'];

/**
 * Whether the datasource is a forecast, as opposed to the actuals datasource
 * (which has no forecast id or name in the response).
 */
export const isForecastDatasource = (datasource: AvailableDatasource) =>
  FORECAST_DATASOURCE_KINDS.includes(datasource.kind);

/**
 * Whether the user has no access to the datasource. Such datasources can still be used by widgets that
 * already reference them, but cannot be newly selected.
 */
export const hasNoAccess = (forecast: Pick<ReportBuilderForecast, 'permission_info'>) =>
  forecast.permission_info?.effective_permission === 'no-access';

/**
 * Maps the forecast datasources returned for a report to the forecast shape used by the report builder.
 * Only the id, name, forecast from date and permission info are provided by the available datasources endpoint.
 */
export const getForecastsFromDatasources = (datasources: AvailableDatasource[]): ReportBuilderForecast[] =>
  datasources
    .filter(isForecastDatasource)
    .map(
      ({ forecast_id, name, forecast_from_date, permission_info }) =>
        ({ id: forecast_id, name, forecast_from_date, permission_info } as ReportBuilderForecast)
    );

export const filterReportBuilderForecasts = (
  forecasts: ReportBuilderForecast[],
  isResourceSharingEnabled: boolean
): ReportBuilderForecast[] =>
  forecasts.filter(forecast => {
    if ('is_hidden' in forecast && forecast.is_hidden) {
      return false;
    }

    if (!isResourceSharingEnabled) {
      return forecast.status === 'published';
    }

    const permission = forecast.permission_info?.effective_permission;
    return permission === 'view' || permission === 'edit';
  });
